import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { audioDownloadSrc, audioSrc, hostAvatarUrl } from './podcastApi';
import { formatDuration } from './podcastJob';

// Wynik gotowego zadania: odtwarzacz jak na landing page (fala, suwak, prędkość), pobieranie WAV
// i transkrypcja, która podąża za odtwarzaniem (klik w wypowiedź = odtwarzanie od niej).

const WAVE_BARS = 64;
const SPEEDS = [1, 1.25, 1.5, 2, 0.75];
const TURN_GAP = 12;

const WAVE_HEIGHTS = Array.from({ length: WAVE_BARS }, (_, i) => {
  const v = Math.abs(Math.sin(i * 0.7) * 0.55 + Math.sin(i * 0.23 + 1) * 0.45);
  return Math.max(14, Math.round(v * 100));
});

const fmtTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const spokenLength = (text) => String(text || '').replace(/\[[^\]]*\]|<[^>]*>/g, '').replace(/\s+/g, ' ').trim().length;

/**
 * Czas każdej wypowiedzi. TTS nie zwraca znaczników czasu, więc długość nagrania dzielimy
 * proporcjonalnie do długości wypowiedzi (+ stała przerwa). Jeśli API kiedyś zwróci
 * turns[i].start/end (sekundy), używamy ich.
 */
export const computeTurnTimes = (turns, duration) => {
  if (!turns.length || !Number.isFinite(duration) || duration <= 0) return null;
  if (turns.every((turn) => Number.isFinite(turn.start) && Number.isFinite(turn.end))) {
    return turns.map((turn) => [Number(turn.start), Number(turn.end)]);
  }
  const weights = turns.map((turn) => spokenLength(turn.text) + TURN_GAP);
  const total = weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  return weights.map((w) => {
    const start = (acc / total) * duration;
    acc += w;
    return [start, (acc / total) * duration];
  });
};

export const turnIndexAt = (times, time) => {
  if (!times || !times.length) return -1;
  const index = times.findIndex(([start, end]) => time >= start && time < end);
  if (index !== -1) return index;
  return time >= times[times.length - 1][0] ? times.length - 1 : -1;
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-6 w-6">
    <path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.5-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5Z" />
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-6 w-6">
    <rect x="6" y="5" width="4" height="14" rx="1.2" />
    <rect x="14" y="5" width="4" height="14" rx="1.2" />
  </svg>
);

const Avatar = ({ hostId, speaker, name }) => {
  const [failed, setFailed] = useState(false);
  const tone = speaker === 'B' ? 'bg-blue-100 text-blue-700' : 'bg-primary-100 text-primary-700';
  if (hostId && !failed) {
    return (
      <img
        src={hostAvatarUrl(hostId)}
        alt=""
        aria-hidden="true"
        onError={() => setFailed(true)}
        className="h-8 w-8 flex-none rounded-full object-cover"
      />
    );
  }
  return (
    <span aria-hidden="true" className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-semibold ${tone}`}>
      {String(name || speaker || '?').slice(0, 1).toUpperCase()}
    </span>
  );
};

const PodcastResult = ({ job, speakerNames = {} }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const turnsRef = useRef(null);
  const seekingRef = useRef(false);
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(Number(job.durationSeconds) || 0);
  const [speed, setSpeed] = useState(1);
  const [following, setFollowing] = useState(false);
  const [currentTurn, setCurrentTurn] = useState(-1);

  const src = audioSrc(job);
  const turns = useMemo(() => (Array.isArray(job.turns) ? job.turns : []), [job.turns]);
  const times = useMemo(() => computeTurnTimes(turns, duration), [turns, duration]);
  const ratio = duration > 0 ? Math.min(1, current / duration) : 0;
  const lit = Math.round(ratio * WAVE_BARS);
  const hostIds = { A: job.hostA || null, B: job.hostB || null };

  const speakerLabel = (speaker) => {
    if (speakerNames[speaker]) return speakerNames[speaker];
    if (speaker === 'A' || speaker === 'B') return t(`podcasts.speakers.${speaker}`);
    return speaker;
  };

  // Tylko jedno audio naraz (np. próbki głosów prowadzących w formularzu).
  useEffect(() => {
    const audio = audioRef.current;
    return () => { try { audio && audio.pause(); } catch (_) { /* ignore */ } };
  }, []);

  // Podświetlona wypowiedź przewija się tylko wewnątrz listy, nigdy całej strony.
  useEffect(() => {
    const box = turnsRef.current;
    if (!box || currentTurn < 0) return;
    const item = box.children[currentTurn];
    if (!item) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top < box.scrollTop || bottom > box.scrollTop + box.clientHeight) {
      const target = Math.max(0, top - box.clientHeight / 3);
      if (typeof box.scrollTo === 'function') box.scrollTo({ top: target, behavior: 'smooth' });
      else box.scrollTop = target;
    }
  }, [currentTurn]);

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!seekingRef.current) setCurrent(audio.currentTime);
    if (audio.currentTime > 0) setFollowing(true);
    setCurrentTurn(turnIndexAt(times, audio.currentTime));
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      const played = audio.play();
      if (played && played.catch) played.catch(() => {});
    } else {
      audio.pause();
    }
  };

  const seekTo = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio || !(duration > 0)) return;
    const next = Math.max(0, Math.min(duration - 0.05, seconds));
    audio.currentTime = next;
    setCurrent(next);
    setCurrentTurn(turnIndexAt(times, next));
  }, [duration, times]);

  const playFromTurn = (index) => {
    if (!times || !times[index]) return;
    seekTo(times[index][0] + 0.01);
    setFollowing(true);
    const audio = audioRef.current;
    if (audio && audio.paused) {
      const played = audio.play();
      if (played && played.catch) played.catch(() => {});
    }
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const speedLabel = `${String(speed).replace('.', ',')}x`;
  const durationLabel = formatDuration(job.durationSeconds);

  return (
    <div data-testid="podcast-result">
      {src ? (
        <>
          <div
            role="group"
            aria-label={t('podcasts.result.player')}
            className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4"
          >
            <div className="flex min-w-0 items-center gap-3.5">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? t('podcasts.result.pause') : t('podcasts.result.play')}
                aria-pressed={playing}
                className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-primary-600 text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                data-testid="podcast-play"
              >
                {playing ? <PauseIcon /> : <PlayIcon />}
              </button>
              <div
                className="flex h-11 min-w-0 flex-1 cursor-pointer items-center gap-0.5 overflow-hidden"
                aria-hidden="true"
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  if (rect.width > 0) seekTo(((event.clientX - rect.left) / rect.width) * duration);
                }}
              >
                {WAVE_HEIGHTS.map((height, i) => (
                  <i
                    key={i}
                    style={{ height: `${height}%`, flex: '1 0 2px', maxWidth: 5 }}
                    className={`block rounded-sm transition-colors ${i < lit ? 'bg-primary-600' : 'bg-neutral-300'}`}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span className="w-10 text-sm tabular-nums text-neutral-600" data-testid="podcast-time">{fmtTime(current)}</span>
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={(ratio * 100).toFixed(1)}
                aria-label={t('podcasts.result.seek')}
                aria-valuetext={`${fmtTime(current)} / ${fmtTime(duration)}`}
                onChange={(event) => {
                  seekingRef.current = true;
                  seekTo((Number(event.target.value) / 100) * duration);
                }}
                onMouseUp={() => { seekingRef.current = false; }}
                onTouchEnd={() => { seekingRef.current = false; }}
                onKeyUp={() => { seekingRef.current = false; }}
                className="h-6 min-w-0 flex-1 cursor-pointer bg-transparent accent-primary-700"
              />
              <span className="w-10 text-right text-sm tabular-nums text-neutral-600">{fmtTime(duration)}</span>
              <button
                type="button"
                onClick={cycleSpeed}
                aria-label={t('podcasts.result.speed', { speed: speedLabel })}
                className="min-w-[52px] rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-sm font-medium text-neutral-900 hover:bg-neutral-100"
              >
                {speedLabel}
              </button>
            </div>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- transkrypcja jest pokazana niżej */}
            <audio
              ref={audioRef}
              preload="metadata"
              src={src}
              data-testid="podcast-audio"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onLoadedMetadata={(event) => {
                const d = event.currentTarget.duration;
                if (Number.isFinite(d) && d > 0) setDuration(d);
              }}
              onTimeUpdate={onTimeUpdate}
            >
              {t('podcasts.result.audioUnsupported')}
            </audio>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <a href={audioDownloadSrc(job)} download={`podcast-${job.id}.wav`} className="btn btn-secondary btn-sm">
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('podcasts.result.download')}
            </a>
            {durationLabel && (
              <span className="text-sm text-gray-500">
                {t('podcasts.result.duration')}: {durationLabel}
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">{t('podcasts.result.noAudio')}</p>
      )}

      <div className="mt-6 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => setTranscriptOpen((open) => !open)}
          aria-expanded={transcriptOpen}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${transcriptOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
          {transcriptOpen ? t('podcasts.result.hideTranscript') : t('podcasts.result.showTranscript')}
        </button>
        {transcriptOpen && (
          turns.length > 0 ? (
            <ol
              ref={turnsRef}
              className="relative mt-3 grid max-h-[420px] list-none gap-2 overflow-y-auto p-0 pr-1"
              data-testid="podcast-transcript"
            >
              {turns.map((turn, index) => {
                const speaker = turn.speaker === 'B' ? 'B' : 'A';
                const isCurrent = index === currentTurn;
                const currentStyle = speaker === 'B'
                  ? 'bg-blue-50 border-l-blue-600'
                  : 'bg-primary-50 border-l-primary-600';
                return (
                  <li
                    key={index}
                    tabIndex={0}
                    title={t('podcasts.result.playFrom')}
                    aria-current={isCurrent ? 'true' : undefined}
                    onClick={() => playFromTurn(index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        playFromTurn(index);
                      }
                    }}
                    className={`grid cursor-pointer grid-cols-[auto_1fr] items-start gap-3 rounded-lg border-l-[3px] px-2 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                      isCurrent ? currentStyle : 'border-l-transparent hover:bg-neutral-50'
                    } ${following && !isCurrent ? 'opacity-60' : ''}`}
                  >
                    <Avatar hostId={hostIds[speaker]} speaker={speaker} name={speakerLabel(speaker)} />
                    <div className="min-w-0">
                      <div className={`mb-0.5 text-sm font-semibold ${speaker === 'B' ? 'text-blue-700' : 'text-primary-700'}`}>
                        {speakerLabel(turn.speaker)}
                      </div>
                      <p className="m-0 text-base text-neutral-800 [overflow-wrap:anywhere]">{turn.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="mt-3 text-sm text-gray-500">{t('podcasts.result.noTranscript')}</p>
          )
        )}
      </div>
    </div>
  );
};

export default PodcastResult;
